// ReportLayoutPage.jsx (FULL UPDATED FILE)
// ✅ Supports remove image + click Update => deletes from DB + S3 (via removeFields[])
// ✅ Uses credentials:"include" on all requests (if you use cookie auth)
// ✅ Better error handling + clears removeFields after save

import React, { useEffect, useState } from "react";
import { Upload, Edit2, Trash2, Check, X, Layout, Type, Save, Loader2 } from "lucide-react";
import API_BASE_URL from "../../../services/api";

const API = `${API_BASE_URL}/api/report-layouts`;

export default function ReportLayoutPage() {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const [previewHeader, setPreviewHeader] = useState(null);
  const [previewFooter, setPreviewFooter] = useState(null);
  const [previewFrontLast, setPreviewFrontLast] = useState(null);
  const [previewLastPage, setPreviewLastPage] = useState(null);

  // ✅ track which fields user wants to remove (trash button)
  const [removeFields, setRemoveFields] = useState([]);

  const [form, setForm] = useState({
    title: "",
    headerImg: null,
    footerImg: null,
    frontPageLastImg: null,
    lastPageImg: null,
  });

  /* ----------------------------------
     FETCH SINGLE LAYOUT
  ---------------------------------- */
  const fetchLayout = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(API, { credentials: "include" });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || `Failed to load report layout (${res.status})`);
        setLayout(null);
        setLoading(false);
        return;
      }

      setLayout(data.layout || null);

      if (data.layout) {
        setForm({
          title: data.layout.title || "",
          headerImg: null,
          footerImg: null,
          frontPageLastImg: null,
          lastPageImg: null,
        });

        setPreviewHeader(data.layout.headerImg || null);
        setPreviewFooter(data.layout.footerImg || null);
        setPreviewFrontLast(data.layout.frontPageLastImg || null);
        setPreviewLastPage(data.layout.lastPageImg || null);
      } else {
        setForm({
          title: "",
          headerImg: null,
          footerImg: null,
          frontPageLastImg: null,
          lastPageImg: null,
        });
        setPreviewHeader(null);
        setPreviewFooter(null);
        setPreviewFrontLast(null);
        setPreviewLastPage(null);
      }

      // ✅ clear removals when refreshing
      setRemoveFields([]);
    } catch (err) {
      console.error(err);
      setError("Failed to load report layout");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchLayout();
  }, []);

  /* ----------------------------------
     IMAGE HANDLER
  ---------------------------------- */
  const handleImageChange = (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setSuccess("");

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size should be less than 5MB");
      return;
    }

    // ✅ if user uploads a new file for a field, cancel removal request for that field
    setRemoveFields((prev) => prev.filter((f) => f !== field));

    setForm((prev) => ({ ...prev, [field]: file }));

    const preview = URL.createObjectURL(file);
    if (field === "headerImg") setPreviewHeader(preview);
    if (field === "footerImg") setPreviewFooter(preview);
    if (field === "frontPageLastImg") setPreviewFrontLast(preview);
    if (field === "lastPageImg") setPreviewLastPage(preview);
  };

  /* ----------------------------------
     CLEAR IMAGE PREVIEW (REQUEST DELETE)
  ---------------------------------- */
  const clearImagePreview = (field) => {
    setError("");
    setSuccess("");

    // ✅ remove selected file if any
    setForm((prev) => ({ ...prev, [field]: null }));

    // ✅ request backend to delete old image too (when updating)
    setRemoveFields((prev) => (prev.includes(field) ? prev : [...prev, field]));

    if (field === "headerImg") setPreviewHeader(null);
    if (field === "footerImg") setPreviewFooter(null);
    if (field === "frontPageLastImg") setPreviewFrontLast(null);
    if (field === "lastPageImg") setPreviewLastPage(null);
  };

  /* ----------------------------------
     SUBMIT (CREATE OR UPDATE)
  ---------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitLoading(true);
    setError("");
    setSuccess("");

    try {
      const fd = new FormData();

      const t = form.title?.trim();
      fd.append("title", t);

      // ✅ tell backend which fields to delete (trash click)
      removeFields.forEach((f) => fd.append("removeFields[]", f));

      // upload new images if provided
      if (form.headerImg) fd.append("headerImg", form.headerImg);
      if (form.footerImg) fd.append("footerImg", form.footerImg);
      if (form.frontPageLastImg) fd.append("frontPageLastImg", form.frontPageLastImg);
      if (form.lastPageImg) fd.append("lastPageImg", form.lastPageImg);

      const res = await fetch(API, {
        method: layout ? "PUT" : "POST",
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || `Failed to save layout (${res.status})`);
      } else {
        setSuccess("Report layout saved successfully");
        setRemoveFields([]); // ✅ clear after successful save
        await fetchLayout();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save layout");
    } finally {
      setSubmitLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  /* ----------------------------------
     DELETE ENTIRE LAYOUT
  ---------------------------------- */
  const handleDeleteLayout = async () => {
    const ok = window.confirm(
      "Are you sure you want to delete the entire report layout? This action cannot be undone."
    );
    if (!ok) return;

    setError("");
    setSuccess("");
    setSubmitLoading(true);

    try {
      const res = await fetch(API, {
        method: "DELETE",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data.error || `Delete failed (${res.status})`);
        return;
      }

      setLayout(null);
      setRemoveFields([]);
      setForm({
        title: "",
        headerImg: null,
        footerImg: null,
        frontPageLastImg: null,
        lastPageImg: null,
      });
      setPreviewHeader(null);
      setPreviewFooter(null);
      setPreviewFrontLast(null);
      setPreviewLastPage(null);

      setSuccess(data.message || "Layout deleted successfully");
    } catch (err) {
      console.error(err);
      setError("Failed to delete layout");
    } finally {
      setSubmitLoading(false);
      setTimeout(() => setSuccess(""), 3000);
    }
  };

  /* ----------------------------------
     UI
  ---------------------------------- */
  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" />
          <p className="mt-2 text-xs text-gray-500">Loading layout...</p>
        </div>
      </div>
    );
  }

  const cards = [
    ["headerImg", "Header Image", previewHeader, "Top of every page"],
    ["footerImg", "Footer Image", previewFooter, "Bottom of every page"],
    ["frontPageLastImg", "First Page Ad", previewFrontLast, "Full ad on first page"],
    ["lastPageImg", "Last Page Ad", previewLastPage, "Full ad on last page"],
  ];

  return (
    <div className="min-h-screen">
      <div>
        {/* Status Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2 text-xs">
            <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-xs">
            <X className="h-3.5 w-3.5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* RemoveFields indicator (optional helpful) */}
        {removeFields.length > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-700">
            You marked {removeFields.length} image(s) to be removed. Click Update to apply.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="bg-white overflow-hidden">
            <div className="p-4 md:p-5">
              {/* Title Section */}
              <div className="mb-6">
                <label className="block text-xs font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Type className="h-3.5 w-3.5 text-gray-500" />
                  Layout Title
                </label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  required
                  placeholder="Enter layout title"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <p className="mt-1.5 text-[11px] text-gray-500">Title helps identify your layout</p>
              </div>

              {/* Images Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                {cards.map(([field, label, preview, description]) => {
                  const isMarkedRemoved = removeFields.includes(field);
                  const hasExisting = !!(layout && layout[field]);

                  return (
                    <div key={field} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                          {label}
                        </label>
                        <p className="text-[11px] text-gray-500 mb-2">{description}</p>

                        <div className="relative">
                          <input
                            id={field}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(field, e)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          />
                          <label
                            htmlFor={field}
                            className="flex flex-col items-center justify-center border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-all duration-150 cursor-pointer group"
                          >
                            <Upload className="h-5 w-5 text-gray-400 group-hover:text-blue-500 mb-1.5" />
                            <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600">
                              Upload image
                            </span>
                            <span className="text-[10px] text-gray-400 mt-0.5">Max 5MB</span>
                          </label>
                        </div>
                      </div>

                      {/* Preview (new upload OR existing url) */}
                      {preview && (
                        <div className="relative group">
                          <div className="border rounded-md overflow-hidden bg-gray-50">
                            <img src={preview} alt={label} className="w-full h-36 object-contain p-1.5" />
                          </div>

                          <div className="absolute top-1.5 right-1.5 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <button
                              type="button"
                              onClick={() => clearImagePreview(field)}
                              className="bg-red-500 text-white p-1 rounded-md hover:bg-red-600 transition-colors"
                              title="Remove image"
                            >
                              <Trash2 className="h-2.5 w-2.5" />
                            </button>
                          </div>

                          <p className="text-[10px] text-gray-400 mt-1.5 text-center">
                            {isMarkedRemoved ? "Marked for removal" : "Preview"}
                          </p>
                        </div>
                      )}

                      {/* Existing image indicator */}
                      {!preview && layout && layout[field] && (
                        <div className="flex items-center justify-between text-[10px]">
                          <span className={`italic ${isMarkedRemoved ? "text-red-500" : "text-gray-400"}`}>
                            {isMarkedRemoved ? "Marked for removal (click Update)" : "Using existing image"}
                          </span>

                          {/* show remove button even for existing */}
                          <button
                            type="button"
                            onClick={() => clearImagePreview(field)}
                            className={`px-2 py-1 rounded-md border text-[10px] flex items-center gap-1 ${
                              isMarkedRemoved
                                ? "bg-red-50 border-red-200 text-red-600"
                                : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                            }`}
                            title="Remove existing image"
                          >
                            <Trash2 className="h-3 w-3" />
                            Remove
                          </button>
                        </div>
                      )}

                      {/* If no existing and no preview */}
                      {!preview && !hasExisting && (
                        <div className="text-[10px] text-gray-400">No image uploaded yet</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-4 py-2.5 rounded-md transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitLoading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-3 w-3" />
                      {layout ? "Update Layout" : "Create Layout"}
                    </>
                  )}
                </button>

                {layout && (
                  <button
                    type="button"
                    onClick={handleDeleteLayout}
                    className="flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium px-4 py-2.5 rounded-md transition-all duration-150 border border-red-200 hover:border-red-300"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete Layout
                  </button>
                )}

                <button
                  type="button"
                  onClick={fetchLayout}
                  className="flex items-center justify-center gap-1.5 text-gray-600 hover:text-gray-800 text-xs font-medium px-4 py-2.5 rounded-md transition-all duration-150 hover:bg-gray-100"
                >
                  <Edit2 className="h-3 w-3" />
                  Refresh
                </button>
              </div>
            </div>

            {/* Info Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-4 md:px-5 py-3">
              <div className="flex items-start gap-2.5">
                <div className="bg-blue-100 p-1.5 rounded-md">
                  <Layout className="h-3 w-3 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700">Layout Tips</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    • Recommended: 1200×200px for header/footer
                    <br />
                    • Use PNG for best quality
                    <br />
                    • Preview before finalizing
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Note */}
        <p className="mt-3 text-[11px] text-gray-500">
          Note: Removing an image requires backend support using <code>removeFields[]</code> in the update API.
        </p>
      </div>
    </div>
  );
}
